import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  UnorderedList,
  ListItem,
  Text,
  Flex,
} from "@chakra-ui/react";

const parseServiceStatus = (stdout = "") => {
  const lines = stdout.split("\n");
  const get = (label) =>
    lines
      .find((l) => l.includes(label))
      ?.split(":")?.[1]
      ?.trim() || "Unknown";

  const statusLine = lines.find((l) => l.includes("Active:")) || "";
  const since = statusLine.match(/since\s(.+?);/)?.[1] ?? "Unknown";
  const memory = get("Memory");
  const cpu = get("CPU");
  const pid = get("Main PID");
  const logs = lines
    .filter((l) => l.includes("GET:"))
    .slice(-5)
    .map((l) => l.replace("info:", "").trim());

  return { since, memory, cpu, pid, logs };
};

const parseJournalLogs = (stdout = "") =>
  stdout
    .split("\n")
    .filter((l) => l.trim().length > 0)
    .slice(-150);

const ServicePanel = () => {
  const [services, setServices] = useState([]);
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiOutput, setApiOutput] = useState("");

  useEffect(() => {
    const fetchCodesets = async () => {
      try {
        const svcRes = await fetch(
          "https://codex.mrc.ai/api/codelists/MRCAI005"
        );
        const cmdRes = await fetch(
          "https://codex.mrc.ai/api/codelists/MRCAI006"
        );
        const svcData = await svcRes.json();
        const cmdData = await cmdRes.json();

        // Sort alphabetically by description1 (fallback to code)
        const sortedServices = (svcData.codes || []).sort((a, b) =>
          (a.description1 || a.code || "").localeCompare(
            b.description1 || b.code || ""
          )
        );

        setServices(sortedServices);
        setCommands(cmdData.codes || []);
      } catch (err) {
        console.error("Failed to fetch codesets", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCodesets();
  }, []);

  const handleExecute = (serviceCode, commandRaw) => {
    if (!serviceCode || !commandRaw) {
      setApiOutput("Invalid service or command.");
      return;
    }

    const [command, serviceOverride] = String(commandRaw).split(":");
    const finalService = serviceOverride || serviceCode;
    const method = command.toLowerCase() === "restart" ? "POST" : "GET";
    const url =
      command.toLowerCase() === "journal"
        ? `https://pipes.mrc.ai/api/journal/${finalService}`
        : `https://pipes.mrc.ai/api/${command}/${finalService}`;

    fetch(url, { method })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        const cmd = command.toLowerCase();
        if (cmd === "status") {
          const parsed = parseServiceStatus(data.stdout);
          setApiOutput(
            <Box>
              <Heading size="sm" mb={2}>{`Status: ${finalService}`}</Heading>
              <Text>
                <b>🟢 Running since:</b> {parsed.since}
              </Text>
              <Text>
                <b>📌 Main PID:</b> {parsed.pid}
              </Text>
              <Text>
                <b>🧠 Memory:</b> {parsed.memory}
              </Text>
              <Text>
                <b>⚙️ CPU:</b> {parsed.cpu}
              </Text>
              <Text mt={2}>
                <b>📄 Recent Logs:</b>
              </Text>
              <UnorderedList pl={4}>
                {parsed.logs.map((line, i) => (
                  <ListItem key={i}>{line}</ListItem>
                ))}
              </UnorderedList>
            </Box>
          );
        } else if (cmd === "journal") {
          const logLines = parseJournalLogs(data.stdout);
          setApiOutput(
            <Box>
              <Heading
                size="sm"
                mb={2}>{`Journal Logs: ${finalService}`}</Heading>
              <UnorderedList pl={4}>
                {logLines.map((line, i) => (
                  <ListItem key={i}>{line}</ListItem>
                ))}
              </UnorderedList>
            </Box>
          );
        } else {
          setApiOutput(<pre>{JSON.stringify(data, null, 2)}</pre>);
        }
      })
      .catch((error) => {
        setApiOutput(error.message);
      });
  };

  if (loading) return <Spinner />;

  return (
    <Flex gap={6} align="start">
      {/* Left: Fixed-width Service Table */}
      <Box
        flexShrink={0}
        maxW="550px"
        borderWidth="1px"
        borderRadius="md"
        p={4}
        overflowX="auto">
        <Heading size="md" mb={4}>
          Platform Services
        </Heading>
        <Table variant="striped" size="sm">
          <Thead>
            <Tr>
              <Th>Service</Th>
              <Th>Commands</Th>
            </Tr>
          </Thead>
          <Tbody>
            {services.map((service) => {
              const serviceCode = service.code || service.longCode;
              return (
                <Tr key={serviceCode}>
                  <Td>{service.description1 || serviceCode}</Td>
                  <Td>
                    <HStack spacing={2} >
                      {commands.map((cmd) => {
                        const commandCode = cmd.code || cmd.longCode;
                        return (
                          <Button
                            key={commandCode}
                            size="xs"
                            colorScheme="linkedin"
                            onClick={() =>
                              handleExecute(serviceCode, commandCode)
                            }>
                            {cmd.description1 || commandCode}
                          </Button>
                        );
                      })}
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Right: Expanding Output Panel */}
      <Box flex="1" borderWidth="1px" borderRadius="md" p={4} minH="300px">
        <Heading size="md" mb={4}>
          Command Output
        </Heading>
        <Box fontSize="sm" color="gray.700">
          {apiOutput || (
            <Text color="gray.400">Select a command to view output</Text>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default ServicePanel;
